import { isBotMatch, matchEndDescriptor, type QgammonEndDescriptor } from '@/lib/qgammon/matchOutcome';
import type { QgammonMatchPlayer, QgammonMatchSummary } from '@/types/qgammon';
import type { FriendshipDTO, SocialRelation } from '@/types/social';

/**
 * Lo storico partite del backgammon (scheda «Partite» della Hub), la parte che non e' disegno: cosa dice una riga e
 * cosa puo' fare chi la guarda. Reference: KB `services/qlash-telegram-app/ui/2026-09-30-storico-backgammon-proposte.md`,
 * «SCELTA di Luca (L-2604) e reference» (tavola a2).
 *
 * Puro di proposito: il banco lo esercita senza montare niente, e un componente che decide da se' cosa mostrare e'
 * un componente che nessun test puo' contraddire.
 */

/* ─────────────── la riga ─────────────── */

export type EsitoStorico = 'won' | 'lost' | 'voided' | 'abandoned' | 'inprogress' | 'unknown';

export interface AvversarioStorico {
  /** Il nome da mostrare, o `null` = «Giocatore» con un avatar neutro. */
  nome: string | null;
  avatarUrl: string | null;
  /**
   * Il `sub` Logto dell'avversario, SE il server lo manda. Senza, la riga non apre il profilo e non porta icone
   * (un server piu' vecchio di `qgammon-orcr-7-storico` non lo serve).
   */
  sub: string | null;
}

export interface RigaStorico {
  esito: EsitoStorico;
  avversario: AvversarioStorico;
  vsBot: boolean;
  /** Il MIO PR, solo su una partita conclusa e analizzata. */
  pr: number | null;
  /** Il replay c'e' solo per una partita CONCLUSA: «Interrotta» e «Annullata» non ne hanno. */
  replay: boolean;
  /** Solo il motivo che dice qualcosa di piu' della parola dell'esito (mai «Interrotta» due volte). */
  motivo: QgammonEndDescriptor;
  /** Le icone social accanto al nome: mai sull'«Interrotta», mai contro un bot, mai senza `sub`. */
  conIcone: boolean;
  /** Il colore della mia pedina, per il replay. */
  coloreMio: 'W' | 'B' | undefined;
  data: string;
}

function esitoDi(m: QgammonMatchSummary, myId: string | null, mine: boolean): EsitoStorico {
  // `voided` PRIMA di ogni altra cosa: la TMA non lo conosceva e lo prendeva per «in corso».
  if (m.status === 'voided') return 'voided';
  if (m.status === 'abandoned') return 'abandoned';
  if (m.status !== 'completed') return 'inprogress';
  if (!myId || !mine || m.winner === null) return 'unknown';
  return m.winner === myId ? 'won' : 'lost';
}

/**
 * `displayName` del server, altrimenti lo `username` (`lg_…`: il ripiego di un server vecchio), altrimenti nessuno.
 *
 * Il parametro e' STRUTTURALE (I-402, Luca: all'avversario il NOME, mai `lg_<sub>`): non solo `QgammonMatchPlayer` ma
 * qualunque giocatore col suo `displayName`/`username` — per esempio l'`opponent` di `invite:accepted`
 * (`@qlashgeminiz/qgammon-types` 0.16.0: `{ userId, username, displayName, elo }`). Un solo posto che sceglie il nome,
 * invece di un `displayName || username` riscritto a mano per ogni foglio (e scordato in uno: `InviteSheet`).
 */
export function nomeDi(
  p: QgammonMatchPlayer | { displayName?: string | null; username?: string | null } | null | undefined,
): string | null {
  const n = p?.displayName?.trim() || p?.username?.trim();
  return n ? n : null;
}

/**
 * Il nome di una riga (I-282, L-2636): il `displayName` di QSocials per quel `sub` se dice qualcosa (dopo il trim),
 * altrimenti quello di QGammon (`nomeDi`, anche il segnaposto «Player abc4»: chi non ha profilo social resta cosi'),
 * altrimenti `null` = «Giocatore». QSocials manda `null` per chi non ha profilo e per chi blocca il lettore: li' vince QGammon.
 */
export function nomeAvversario(nomeSocial: string | null | undefined, nomeQgammon: string | null): string | null {
  const n = nomeSocial?.trim();
  return n ? n : nomeQgammon;
}

/** La stessa riga col nome deciso da `nomeAvversario`: nome visibile, iniziale dell'avatar, nome accessibile e fogli leggono TUTTI questo valore. */
export function rigaConNomeSocial(riga: RigaStorico, nomeSocial: string | null | undefined): RigaStorico {
  const nome = nomeAvversario(nomeSocial, riga.avversario.nome);
  return nome === riga.avversario.nome ? riga : { ...riga, avversario: { ...riga.avversario, nome } };
}

export function rigaStorico(m: QgammonMatchSummary, myId: string | null): RigaStorico {
  const mine = Boolean(myId) && (m.white?.id === myId || m.black?.id === myId);
  const coloreMio: 'W' | 'B' | undefined = m.white?.id === myId ? 'W' : m.black?.id === myId ? 'B' : undefined;
  const opp = mine ? (m.white?.id === myId ? m.black : m.white) : null;
  const esito = esitoDi(m, myId, mine);
  const vsBot = isBotMatch(m);
  const sub = opp?.logtoSub?.trim() || null;
  const nome = mine
    ? nomeDi(opp)
    : // Una riga che non e' mia non dovrebbe esistere in `/matches/mine`: il ripiego di sempre, i due nomi.
      [nomeDi(m.white) ?? '?', nomeDi(m.black) ?? '?'].join(' · ');
  const motivo = matchEndDescriptor(m);
  return {
    esito,
    avversario: { nome, avatarUrl: opp?.avatarUrl ?? null, sub },
    vsBot,
    // Il MIO PR, e solo su una conclusa: l'«Interrotta» e l'«Annullata» non portano numeri.
    pr: mine && m.status === 'completed' ? (m.white?.id === myId ? m.prWhite : m.prBlack) : null,
    replay: m.status === 'completed',
    motivo,
    conIcone: mine && esito !== 'abandoned' && !vsBot && sub !== null,
    coloreMio,
    data: m.endedAt ?? m.createdAt,
  };
}

/* ─────────────── data e PR ─────────────── */

const MS_GIORNO = 86_400_000;

/**
 * «oggi», «ieri», «N giorni fa» fino a 6, poi giorno e mese (con l'anno se non e' quello corrente), nella lingua
 * dell'app. Il conto e' in GIORNI DI CALENDARIO locali, non in 24 ore: una partita delle 23:50 e' «ieri» alle 00:10.
 * Una data nel futuro (orologi sfasati) e' «oggi», mai «fra N giorni».
 */
export function dataRelativa(iso: string, adesso: Date, lingua: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const giorno = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const fa = Math.max(0, Math.round((giorno(adesso) - giorno(d)) / MS_GIORNO));
  if (fa <= 6) return new Intl.RelativeTimeFormat(lingua, { numeric: 'auto' }).format(-fa, 'day');
  const stessoAnno = d.getFullYear() === adesso.getFullYear();
  return new Intl.DateTimeFormat(lingua, stessoAnno ? { day: 'numeric', month: 'short' } : { day: 'numeric', month: 'short', year: 'numeric' }).format(d);
}

/** Il PR con un decimale e la virgola della lingua («25,6»). */
export function formatPr(pr: number, lingua: string): string {
  return new Intl.NumberFormat(lingua, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(pr);
}

/* ─────────────── le icone e i gesti ─────────────── */

/** Cio' che QSocials sa dell'avversario per chi guarda: la relazione di una riga del lotto. Il nome del lotto viaggia a parte (`nomeAvversario`). */
export interface StatoSocial {
  relation: SocialRelation;
  following: boolean;
}

/** Il gesto che un tocco compie. `profilo` e `sfida` non scrivono niente: aprono una schermata. */
export type Gesto =
  | 'profilo'
  | 'segui'
  | 'nonSeguire'
  | 'richiedi'
  | 'annullaRichiesta'
  | 'accetta'
  | 'rimuovi'
  | 'sfida';

export type PostoIcona = 'profilo' | 'segui' | 'amicizia' | 'sfida';

/** Le parole della legenda, chiavi di `general:social.*` — il nome accessibile del bottone. */
export type ParolaIcona =
  | 'profile'
  | 'follow'
  | 'following'
  | 'addFriend'
  | 'requestPending'
  | 'accept'
  | 'friends'
  | 'challenge';

export interface Icona {
  posto: PostoIcona;
  gesto: Gesto;
  parola: ParolaIcona;
  /** «Accesa»: il fondo brand al 15% (segui gia', amici). «Spenta» e' il fondo secondario. */
  accesa: boolean;
  /** «Brand pieno»: SOLO «Accetta», l'unica icona che chiede di fare qualcosa. */
  piena: boolean;
  /** Il gesto chiede conferma prima di scrivere (solo «Rimuovi amico»). */
  conferma: boolean;
}

const RELAZIONI_CON_GESTI: readonly SocialRelation[] = ['none', 'pending_outgoing', 'pending_incoming', 'accepted'];

/** Le quattro relazioni su cui si disegnano i gesti: `blocked_by_*` e ogni valore futuro = nessuna icona. */
export function relazioneConGesti(r: string): r is 'none' | 'pending_outgoing' | 'pending_incoming' | 'accepted' {
  return (RELAZIONI_CON_GESTI as readonly string[]).includes(r);
}

/**
 * Le icone di una riga, nell'ordine fisso **profilo · segui · amicizia · sfida**, con gli stati e i gesti della
 * tabella della reference. `stato === null` (lotto non arrivato, fallito, server senza la rotta, o sub omesso: un server precedente alla #29 e la
 * rilettura dopo un errore) = nessuna icona; lo stesso per una relazione che non disegna gesti (`blocked_by_me`: il server
 * la MANDA nel lotto). Dopo QSocials #29 il lotto ha una voce per OGNI sub chiesto: chi non ha profilo torna con
 * `displayName: null` e la relazione VERA, chi blocca il lettore con `displayName: null`, `relation: 'none'`, `following: false`
 * (come un mai visto: e ha le icone di `none`).
 * La sfida c'e' solo a `accepted` E con la serratura aperta (`sfidaAccesa`, letta UNA volta per lista).
 */
export function iconeDi(stato: StatoSocial | null, sfidaAccesa: boolean): Icona[] {
  if (stato === null || !relazioneConGesti(stato.relation)) return [];
  const icone: Icona[] = [
    { posto: 'profilo', gesto: 'profilo', parola: 'profile', accesa: false, piena: false, conferma: false },
    stato.following
      ? { posto: 'segui', gesto: 'nonSeguire', parola: 'following', accesa: true, piena: false, conferma: false }
      : { posto: 'segui', gesto: 'segui', parola: 'follow', accesa: false, piena: false, conferma: false },
  ];
  switch (stato.relation) {
    case 'none':
      icone.push({ posto: 'amicizia', gesto: 'richiedi', parola: 'addFriend', accesa: false, piena: false, conferma: false });
      break;
    case 'pending_outgoing':
      icone.push({ posto: 'amicizia', gesto: 'annullaRichiesta', parola: 'requestPending', accesa: false, piena: false, conferma: false });
      break;
    case 'pending_incoming':
      icone.push({ posto: 'amicizia', gesto: 'accetta', parola: 'accept', accesa: false, piena: true, conferma: false });
      break;
    case 'accepted':
      icone.push({ posto: 'amicizia', gesto: 'rimuovi', parola: 'friends', accesa: true, piena: false, conferma: true });
      if (sfidaAccesa) {
        icone.push({ posto: 'sfida', gesto: 'sfida', parola: 'challenge', accesa: false, piena: false, conferma: false });
      }
      break;
  }
  return icone;
}

/**
 * La relazione che dice la RISPOSTA di una scrittura di amicizia (`FriendshipDTO`, `toDTO(me, doc)` in QSocials
 * `modules/friendships/service.ts`): `accepted`; `pending` col verso (`isIncoming` = me l'ha chiesta l'altro);
 * `blocked` non disegna gesti. Un `pending` senza verso e' una richiesta MIA (il caso di `requestFriendship`).
 */
export function relazioneDaAmicizia(a: Pick<FriendshipDTO, 'status' | 'isIncoming' | 'isOutgoing'>): SocialRelation {
  if (a.status === 'accepted') return 'accepted';
  if (a.status === 'blocked') return 'blocked_by_me';
  return a.isIncoming === true && a.isOutgoing !== true ? 'pending_incoming' : 'pending_outgoing';
}

/**
 * Lo stato dopo un gesto RIUSCITO. Serve a tenere UNA sola sorgente per `sub`: il gesto su una riga muove tutte le
 * righe dello stesso avversario senza rileggere il lotto. `profilo` e `sfida` non cambiano niente.
 *
 * 🔴 Per `richiedi` e `accetta` la relazione NON si calcola: la dice la risposta (`amicizia`). Il server decide, e non
 * sempre quello che ci aspettiamo — una richiesta verso chi me l'aveva GIA' fatta diventa un'accettazione (`requestFriendship`,
 * `service.ts:195` @c0d85b5): calcolare `pending_outgoing` lasciava «Richiesta in sospeso» su un'amicizia gia' accettata, e il
 * tocco dopo mandava un DELETE che la toglieva senza conferma. Senza la risposta (non dovrebbe succedere) resta il calcolo.
 */
export function dopoGesto(stato: StatoSocial, gesto: Gesto, amicizia?: Pick<FriendshipDTO, 'status' | 'isIncoming' | 'isOutgoing'>): StatoSocial {
  switch (gesto) {
    case 'segui':
      return { ...stato, following: true };
    case 'nonSeguire':
      return { ...stato, following: false };
    case 'richiedi':
      return { ...stato, relation: amicizia ? relazioneDaAmicizia(amicizia) : 'pending_outgoing' };
    case 'accetta':
      return { ...stato, relation: amicizia ? relazioneDaAmicizia(amicizia) : 'accepted' };
    case 'annullaRichiesta':
    case 'rimuovi':
      return { ...stato, relation: 'none' };
    default:
      return stato;
  }
}

/**
 * I `sub` da chiedere a QSocials per una pagina: ogni riga con un avversario umano e un `sub` (il nome QSocials serve a
 * tutte, le icone solo a `conIcone`), deduplicati, in ordine stabile. L'«Interrotta» entra per il SOLO nome: `conIcone`
 * resta falso e non disegna icone. Mai un bot, mai un server vecchio senza `sub`.
 */
export function subsDaRisolvere(righe: readonly RigaStorico[]): string[] {
  const s = new Set<string>();
  for (const r of righe) if (!r.vsBot && r.avversario.sub) s.add(r.avversario.sub);
  return [...s].sort();
}
